// Pure operations on the programme tree (phase → week → day).
//
// The rows are a flat adjacency list, exactly as stored, so every operation here maps to
// a small set of row updates rather than a document rewrite — which is the reason the
// tree is normalised in the first place.

import type { ProgramNode, Uuid } from './types';

export interface TreeNode {
    node: ProgramNode;
    children: TreeNode[];
    depth: number;
}

/** Build the nested view from flat rows, ordered by sort_order at every level. */
export function buildTree(nodes: ProgramNode[], rootId: Uuid | null): TreeNode[] {
    const byParent = new Map<Uuid | null, ProgramNode[]>();
    for (const node of nodes) {
        const siblings = byParent.get(node.parent_id) ?? [];
        siblings.push(node);
        byParent.set(node.parent_id, siblings);
    }
    for (const siblings of byParent.values()) {
        siblings.sort((a, b) => a.sort_order - b.sort_order || a.title.localeCompare(b.title));
    }

    const build = (parentId: Uuid | null, depth: number): TreeNode[] =>
        (byParent.get(parentId) ?? []).map((node) => ({
            node,
            depth,
            children: build(node.id, depth + 1),
        }));

    // The root row itself is a node, so start from its parent and descend into it.
    if (rootId) {
        const root = nodes.find((n) => n.id === rootId);
        if (root) return [{ node: root, depth: 0, children: build(root.id, 1) }];
    }
    return build(null, 0);
}

/** Depth-first flattening, for rendering an indented list. */
export function flatten(tree: TreeNode[]): TreeNode[] {
    return tree.flatMap((node) => [node, ...flatten(node.children)]);
}

export type LevelName = 'Programme' | 'Phase' | 'Week' | 'Day';

/**
 * What a node at this depth is called. Naming is by position rather than a stored type,
 * because the same recursive structure also has to describe a flat library — where depth
 * 1 is simply a session, not a "phase".
 */
export function levelName(depth: number): LevelName {
    return (['Programme', 'Phase', 'Week', 'Day'] as const)[Math.min(depth, 3)]!;
}

export interface Reorder {
    id: Uuid;
    sort_order: number;
}

/**
 * Move a node up or down among its siblings, returning the rows whose sort_order changed.
 *
 * Reordering is the primary edit on a programme, so this returns a minimal diff rather
 * than renumbering the whole tree. Siblings are renumbered densely from 0 so repeated
 * moves cannot drift into collisions.
 */
export function moveSibling(
    nodes: ProgramNode[],
    id: Uuid,
    direction: 'up' | 'down',
): Reorder[] {
    const target = nodes.find((n) => n.id === id);
    if (!target) return [];

    const siblings = nodes
        .filter((n) => n.parent_id === target.parent_id)
        .sort((a, b) => a.sort_order - b.sort_order);

    const index = siblings.findIndex((n) => n.id === id);
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (index < 0 || swapWith < 0 || swapWith >= siblings.length) return [];

    const reordered = siblings.slice();
    const [moved] = reordered.splice(index, 1);
    reordered.splice(swapWith, 0, moved!);

    return reordered
        .map((node, position) => ({ id: node.id, sort_order: position }))
        .filter((row) => {
            const original = siblings.find((s) => s.id === row.id);
            return original && original.sort_order !== row.sort_order;
        });
}

/** Next sort_order for a new child, so additions land at the end. */
export function nextSortOrder(nodes: ProgramNode[], parentId: Uuid | null): number {
    const siblings = nodes.filter((n) => n.parent_id === parentId);
    return siblings.length === 0 ? 0 : Math.max(...siblings.map((n) => n.sort_order)) + 1;
}

/** Every descendant id, for a delete confirmation that can say how much goes with it. */
export function descendantIds(nodes: ProgramNode[], id: Uuid): Uuid[] {
    const direct = nodes.filter((n) => n.parent_id === id).map((n) => n.id);
    return direct.flatMap((childId) => [childId, ...descendantIds(nodes, childId)]);
}

/** Sessions referenced anywhere beneath a node, in traversal order. */
export function sessionIds(tree: TreeNode[]): Uuid[] {
    return flatten(tree)
        .map((n) => n.node.session_id)
        .filter((id): id is Uuid => id !== null);
}
